import type { ReactElement } from 'react';
import type { ExhibitionPurposeView } from '@/entities/exhibition';
import { cn } from '@/shared/ui';

interface ExhibitionPurposeTabsProps {
  readonly purposes: readonly ExhibitionPurposeView[];
  readonly selectedId: string;
  readonly onSelect: (purposeId: string) => void;
}

/**
 * 기획전 목적 탭 — exhibitions.list 가 준 순서 그대로. 스토어 카테고리 탭과 같은 모양으로
 * 전폭 얇은 선 위에 한 줄로 두고 선택한 탭은 굵은 2px 밑줄로 표시한다. 좁은 화면에서는 가로로 밀어 본다.
 * 공개 기획전이 0개인 목적도 탭에 남긴다(확정 규칙) — 누르면 카드 자리에 준비 중 안내가 뜬다.
 */
export const ExhibitionPurposeTabs = ({
  purposes,
  selectedId,
  onSelect,
}: ExhibitionPurposeTabsProps): ReactElement => (
  <nav aria-label="기획전 목적">
    {/* 탭이 넘치면 가로로 민다 — 얇은 선은 스크롤 폭 전체에 걸치도록 목록 자신에 긋는다. */}
    <div className="-mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0">
      <ul className="flex w-max min-w-full gap-7 border-b border-foreground/10 sm:gap-10">
        {purposes.map((purpose) => {
          const isSelected = purpose.id === selectedId;

          return (
            // 밑줄은 탭 줄의 얇은 선(1px) 위에 겹쳐 앉는다(-mb-px) — 선택한 탭이 선을 끊고 굵게 이어지는 모양.
            <li key={purpose.id} className="-mb-px shrink-0">
              <button
                type="button"
                aria-current={isSelected ? 'true' : undefined}
                onClick={() => onSelect(purpose.id)}
                className={cn(
                  'flex h-12 cursor-pointer items-center border-b-2 text-[15px] whitespace-nowrap transition-colors sm:text-base',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isSelected
                    ? 'border-foreground font-bold text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {purpose.name}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  </nav>
);
